import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { voidRetainerAgreement } from '@/services/LegalDocumentService';

interface Props {
    prospectId: string;
    contactId: string;
    clientName: string;
    onClose: () => void;
    onVoided: () => void;
}

// A void is kept on record with its reason. The client is asked to sign again on their form
export default function VoidRetainerDialog({
    prospectId,
    contactId,
    clientName,
    onClose,
    onVoided,
}: Props) {
    const [reason, setReason] = useState('');

    const voidMutation = useMutation({
        mutationFn: () => voidRetainerAgreement({ prospectId, contactId, reason: reason.trim() }),
        onSuccess: onVoided,
    });

    return (
        <Dialog open onClose={voidMutation.isPending ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Void signed retainer</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText>
                        {`The retainer signed for ${clientName || 'this client'} will no longer count as signed and will be removed from their documents. It stays on record with your reason. If the prospect still has a retainer, the client will be asked to sign it when they next open their form.`}
                    </DialogContentText>
                    <TextField
                        label="Reason"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        required
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 1000 }}
                        disabled={voidMutation.isPending}
                        autoFocus
                    />
                    {voidMutation.isError && (
                        <Alert severity="error">
                            Failed to void the signed retainer. Please try again.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose} disabled={voidMutation.isPending}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color="error"
                    loading={voidMutation.isPending}
                    disabled={!reason.trim() || voidMutation.isPending}
                    onClick={() => voidMutation.mutate()}
                >
                    Void retainer
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
